// frontend/src/hooks/useAnalyzeToolState.ts
// Single source of truth for Analyze tool state (Piece C). Consumed by:
//   1. AnalyzeToolNest (left rail)   — no searchText
//   2. AnalyzeOverview via analyze.tsx
//   3. analyze.tsx panels/handlers   — passes its debounced local search
// READ-ONLY: never fires mutations; pending flags come from global Zustand
// stores + useIsMutating, so every instance sees the same lifecycle state.
// Deliberate divergence: the rail omits searchText, so while a transient
// search is typed the rail reflects concept-level state and the center
// reflects the searched-down lens. Don't "fix" this in either direction.
import { useMemo } from "react";
import { useIsMutating } from "@tanstack/react-query";
import { usePapers } from "@/hooks/usePapers";
import { useFilteredPapers, type PaperFilters } from "@/hooks/useFilteredPapers";
import { useFilterStore } from "@/stores/filter-store";
import { useWorkingSetPaperIds } from "@/hooks/useWorkingSets";
import { useRagIndex } from "@/hooks/useRagIndex";
import { useGapAnalysisStore } from "@/stores/gap-analysis-store";
import { useFieldDebate, type DebateMap } from "@/hooks/useFieldDebate";
import { useFieldDebateGenerationStore } from "@/stores/field-debate-generation-store";
import { useComparison } from "@/hooks/useComparison";
import { useConcept } from "@/hooks/useConcepts";
import { useClaims, useClaimLinks } from "@/hooks/useClaims";
import { useCurrentUser, isPaidPlan } from "@/hooks/useCurrentUser";
import { useLandscapeStore } from "@/stores/useLandscapeStore";
import { divergence, isStale, type ResultFingerprint } from "@/lib/resultFingerprint";
import { isArgumentStale } from "@/lib/argumentFingerprint";
import { needsDebateRefresh } from "@/lib/analyzePipelineState";
import {
  computeToolStatuses,
  computeCritiqueStatuses,
  isBuildInFlight,
  isDebateMapComplete,
  type ToolId,
  type ToolStatusResult,
  type CritiqueStatuses,
} from "@/lib/analyzeToolState";
import type { PaperWithRole } from "@/types/paper";

export interface AnalyzeToolState {
  /** False until the trawled-papers query resolves — gate one-shot param
      consumption (e.g. ?autorun) on this so cold loads don't lose it. */
  papersLoaded: boolean;
  allPapers: PaperWithRole[];
  displayPapers: PaperWithRole[];
  totalPapers: number;
  displayPaperIds: string[];
  /** Trawled minus dismissed — landscape staleness scope. */
  activePaperIds: string[];
  scoredCount: number;
  wsId: string | undefined;
  statuses: Record<ToolId, ToolStatusResult>;
  debateStale: boolean;
  /** Stale AND nothing already in flight to fix it — what the advisor's
   *  refresh_debate rule keys on (Codex round 3: don't re-recommend a
   *  refresh the user already started). */
  debateNeedsRefresh: boolean;
  auditStale: boolean;
  positionStale: boolean;
  landscapeStale: boolean;
  debateOverlapPercent: number;
  auditOverlapPercent: number;
  debateRunning: boolean;
  /** Batch-tail phase of a full build (partial map / pending_build "build"). */
  debateBuildInFlight: boolean;
  debateRefreshing: boolean;
  auditRunning: boolean;
  comparisonRunning: boolean;
  fieldDebateData: DebateMap | undefined;
  gapAnalysisData: ReturnType<typeof useGapAnalysisStore.getState>["data"];
  cachedComparison: ReturnType<typeof useComparison>["data"];
  positionAnalysis: NonNullable<ReturnType<typeof useConcept>["data"]>["position_analysis"] | null;
  chatIndexed: boolean;
  isPaid: boolean;
  hasEvidenceOnClaims: boolean;
  critique: CritiqueStatuses;
  argumentAnalysis: NonNullable<ReturnType<typeof useConcept>["data"]>["argument_analysis"] | null;
  argumentStale: boolean;
}

const EMPTY_FILTERS: PaperFilters = {};

/**
 * Staleness + overlap of a result fingerprint against the current scope.
 * `scopeReady` gates it: while the analytics papers (or the working set's
 * id list) are still loading, the current scope reads as EMPTY and every
 * fingerprint looked 100% drifted — Analyze flashed STALE / "0% overlap" on
 * a cold load of a large corpus. No data yet → no drift claimed.
 */
export function scopeDrift(
  fingerprint: ResultFingerprint | null | undefined,
  currentPaperIds: string[],
  wsId: string | null,
  scopeReady: boolean,
): { stale: boolean; overlapPercent: number } {
  if (!fingerprint) return { stale: false, overlapPercent: 0 }; // nothing to compare
  if (!scopeReady) return { stale: false, overlapPercent: 100 };
  return {
    stale: isStale(fingerprint, { paperIds: currentPaperIds, wsId }, 0),
    overlapPercent: Math.round((1 - divergence(fingerprint, currentPaperIds)) * 100),
  };
}

/**
 * Field-Debate scoping (backend) excludes low-score / over-budget papers
 * from assignment entirely — `fingerprint.paper_ids` reflects only the
 * in-scope subset that was actually run. Those excluded papers are still
 * present in the unscoped display set (they weren't removed from the
 * corpus, just not assigned to a camp), so comparing raw display ids
 * against the fingerprint flagged the map "stale" forever the moment
 * scoping excluded >=1 paper (review finding I1).
 *
 * Re-derive the comparison set from CURRENT scores rather than a frozen
 * per-paper exclusion list (the backend no longer sends one at all — review
 * finding F2): a paper that was below `scope.min_score` at
 * generation time but has since been rescored ABOVE it is legitimately
 * back in the comparison set, is absent from the fingerprint, and so
 * correctly trips staleness — pinning it to a frozen exclusion list made
 * that path a dead end (the OutOfScopePanel copy tells users to rescore,
 * but rescoring above threshold could never un-stick the paper). Papers
 * currently below the threshold are excluded — a rerun wouldn't assign
 * them either, so their absence from the fingerprint isn't staleness.
 * Legacy maps without a `scope` field, and `fallback_all` maps (scoping
 * didn't apply), keep the raw display set — unchanged prior behavior.
 *
 * The mirror must include the TOP-N CAP, not just the threshold: on a
 * cap-bound corpus (more than `max_papers` above threshold) the over-cap
 * papers are >= min_score yet deliberately unassigned, so a threshold-only
 * comparison read every such map as permanently stale (hotfix 2026-07-08).
 * Ordering matches scope_rows exactly: score desc, then id asc.
 */
export function debateComparisonPaperIds(
  displayPapers: Array<{ id: string; normalized_score: number }>,
  scope: DebateMap["scope"] | undefined,
): string[] {
  if (!scope || scope.fallback_all) {
    return displayPapers.map((p) => p.id);
  }
  return displayPapers
    .filter((p) => p.normalized_score >= scope.min_score)
    .sort(
      (a, b) =>
        b.normalized_score - a.normalized_score ||
        (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
    )
    .slice(0, scope.max_papers)
    .map((p) => p.id);
}

/**
 * The complement of the scoping mirror: papers the current scores leave OUT
 * of stance assignment (below threshold, or beyond the top-N cap). Feeds the
 * OutOfScopePanel listing — derived client-side because the map's ScopeMeta
 * deliberately stores only a count (~80KB of ids per build otherwise).
 * Sorted score-descending so the near-misses lead: those are the papers a
 * score adjustment would pull into scope. Current-score semantics on purpose
 * — adjust a score and the paper leaves the list, mirroring how staleness
 * self-heals.
 */
export function outOfScopePapers<
  T extends { id: string; normalized_score: number },
>(displayPapers: T[], scope: DebateMap["scope"] | undefined): T[] {
  if (!scope || scope.fallback_all) return [];
  const inScope = new Set(debateComparisonPaperIds(displayPapers, scope));
  return displayPapers
    .filter((p) => !inScope.has(p.id))
    .sort(
      (a, b) =>
        b.normalized_score - a.normalized_score ||
        (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
    );
}

/** Added-fraction of current scope above which the stale banner appends the
    "consider Rebuild" advisory (FD Stage 3). */
export const DRIFT_ADVISORY_PCT = 0.3;

/**
 * Client-side preview of what POST /refresh will do: papers now in scope but
 * absent from the fingerprint (added) and fingerprinted papers no longer in
 * scope (removed). Uses the SAME mirror as staleness (threshold + cap) so the
 * banner's counts can't disagree with the server's diff.
 */
export function debateRefreshDiff(
  displayPapers: Array<{ id: string; normalized_score: number }>,
  scope: DebateMap["scope"] | undefined,
  fingerprint: DebateMap["fingerprint"] | undefined,
): { added: number; removed: number } {
  if (!fingerprint) return { added: 0, removed: 0 };
  const current = new Set(debateComparisonPaperIds(displayPapers, scope));
  const base = new Set(fingerprint.paper_ids);
  let added = 0;
  for (const id of current) if (!base.has(id)) added++;
  let removed = 0;
  for (const id of base) if (!current.has(id)) removed++;
  return { added, removed };
}

/**
 * True when the current working-set scope differs from the one the map was
 * built under. A scope change is a REBUILD, not a refresh — camps were
 * derived from a scope, so the refresh diff counts are meaningless across
 * scopes and the backend 409s cross-scope refreshes. No fingerprint means
 * nothing to compare (legacy maps): treat as unchanged.
 */
export function debateScopeChanged(
  wsId: string | null | undefined,
  fingerprint: DebateMap["fingerprint"] | undefined,
): boolean {
  if (!fingerprint) return false;
  return (wsId ?? null) !== (fingerprint.ws_id ?? null);
}

export function useAnalyzeToolState(
  conceptId: string,
  opts?: { searchText?: string },
): AnalyzeToolState {
  // --- scope chain (ported from analyze.tsx) ---
  const { data: trawledData, isError: papersError } = usePapers(conceptId, {
    role: "trawled",
    mode: "all",
    shape: "analytics",
  });
  const storedState = useFilterStore((s) => s.filters[conceptId]);
  // Stable fallback — a fresh {} here would bust the mergedFilters memo on
  // every render of every consumer (ConceptRail re-renders on each
  // generation-store tick) and re-run the filter chain for nothing.
  const expandFilters = storedState?.filters ?? EMPTY_FILTERS;
  const expandSortField = storedState?.sortField ?? "relevance_score";
  const expandSortDirection = storedState?.sortDirection ?? "desc";
  const wsId = storedState?.wsId;
  const paperIdScope = storedState?.paperIdScope;
  const { data: wsPaperIdsData, isError: wsPaperIdsError } = useWorkingSetPaperIds(
    conceptId,
    wsId,
  );

  const trawledPapers = useMemo(() => trawledData?.papers ?? [], [trawledData]);
  const allPapers = useMemo(() => {
    if (!wsId || !wsPaperIdsData) return trawledPapers;
    const idSet = new Set(wsPaperIdsData.paper_ids);
    return trawledPapers.filter((p) => idSet.has(p.id));
  }, [trawledPapers, wsId, wsPaperIdsData]);

  const scoredCount = useMemo(
    () => allPapers.filter((p) => p.scoring_timestamp != null).length,
    [allPapers],
  );

  const mergedFilters = useMemo(
    () => ({
      ...expandFilters,
      overrideFilter: expandFilters.overrideFilter ?? "active",
      searchText: opts?.searchText || expandFilters.searchText,
    }),
    [expandFilters, opts?.searchText],
  );

  const { filtered: displayPapers, total: totalPapers } = useFilteredPapers(
    allPapers,
    mergedFilters,
    expandSortField,
    expandSortDirection,
    undefined,
    paperIdScope,
  );
  const displayPaperIds = useMemo(
    () => displayPapers.map((p) => p.id),
    [displayPapers],
  );
  const activePaperIds = useMemo(
    () => allPapers.filter((p) => !p.exclusion_reason).map((p) => p.id),
    [allPapers],
  );

  // --- artifact data (all query/store reads — safe across instances) ---
  const { indexStatus } = useRagIndex(conceptId);
  const fieldDebate = useFieldDebate(conceptId);
  const gapStore = useGapAnalysisStore();
  const gapData = gapStore.conceptId === conceptId ? gapStore.data : null;
  const gapFingerprint =
    gapStore.conceptId === conceptId ? gapStore.fingerprint : null;
  const auditRunning = gapStore.isPending && gapStore.conceptId === conceptId;
  const debateGenStore = useFieldDebateGenerationStore();
  // A full build is in flight: this tab's job (store flag), OR the batch-tail
  // phase after the in-process job ended — the map is a partial skeleton and
  // the server reports pending_build kind "build" (survives reloads). Without
  // the latter the card fell back to READY mid-build (2026-09-24).
  const debateBuildInFlight = isBuildInFlight(fieldDebate.data);
  const debateRunning =
    (debateGenStore.isPending && debateGenStore.conceptId === conceptId) ||
    debateBuildInFlight;
  // A batch refresh is in flight: either this tab submitted it (store flag)
  // or the server says so (pending_build, which survives reloads).
  const debateRefreshing =
    (debateGenStore.pendingRefresh?.conceptId === conceptId ||
      fieldDebate.data?.pending_build?.kind === "refresh") &&
    isDebateMapComplete(fieldDebate.data);
  const { data: cachedComparison } = useComparison(conceptId);
  const comparisonRunning =
    useIsMutating({ mutationKey: ["comparison-generate", conceptId] }) > 0;
  const { data: concept } = useConcept(conceptId);
  const positionAnalysis = concept?.position_analysis ?? null;
  const { data: claimsData } = useClaims(conceptId);
  const hasEvidenceOnClaims =
    claimsData?.some((c) => c.evidence_count > 0) ?? false;
  const { data: linksData } = useClaimLinks(conceptId);
  const argumentAnalysis = concept?.argument_analysis ?? null;
  const argumentRunning =
    useIsMutating({ mutationKey: ["argument-analyze", conceptId] }) > 0;
  const positionRunning =
    useIsMutating({ mutationKey: ["position-analyze", conceptId] }) > 0;
  const { data: currentUser } = useCurrentUser();
  const isPaid = isPaidPlan(currentUser?.plan);
  const cachedLandscapeConceptId = useLandscapeStore((s) => s.cachedConceptId);
  const cachedLandscapeFingerprint = useLandscapeStore((s) => s.cachedFingerprint);

  // --- staleness (threshold=0 everywhere, matching analyze.tsx) ---
  // The scope is only knowable once the papers (and, in a working set, its
  // id list) have loaded — before that, comparisons read an empty scope.
  // A failed query counts as settled: flagging STALE on an unloadable corpus
  // is the conservative failure, never silently reporting results as fresh
  // (Codex review 2026-09-24).
  const scopeReady =
    (trawledData !== undefined || papersError) &&
    (!wsId || wsPaperIdsData !== undefined || wsPaperIdsError);
  const debateDrift = useMemo(
    () =>
      scopeDrift(
        fieldDebate.data?.fingerprint,
        debateComparisonPaperIds(displayPapers, fieldDebate.data?.scope),
        wsId ?? null,
        scopeReady,
      ),
    [fieldDebate.data, displayPapers, wsId, scopeReady],
  );
  const debateStale = debateDrift.stale;
  const debateOverlapPercent = debateDrift.overlapPercent;
  const auditDrift = useMemo(
    () => scopeDrift(gapData ? gapFingerprint : null, displayPaperIds, wsId ?? null, scopeReady),
    [gapFingerprint, gapData, displayPaperIds, wsId, scopeReady],
  );
  const auditStale = auditDrift.stale;
  const auditOverlapPercent = auditDrift.overlapPercent;
  const positionStale = useMemo(() => {
    const fp = positionAnalysis?.fingerprint;
    // Mirror the backend's compute_position_fingerprint: position grounds in
    // the debate map's camp-paper union, NOT the display scope. Comparing
    // against displayPaperIds flagged STALE on every filter/WS tweak even
    // when the debate was unchanged (Codex review 2026-06-07). No debate →
    // can't assess → render fresh (matches the retired panel's guard).
    if (!fp || !fieldDebate.data) return false;
    const campPaperIds = Array.from(
      new Set(fieldDebate.data.camps.flatMap((c) => c.paper_ids ?? [])),
    );
    return isStale(
      fp,
      {
        paperIds: campPaperIds,
        wsId: fieldDebate.data.fingerprint?.ws_id ?? null,
      },
      0,
    );
  }, [positionAnalysis, fieldDebate.data]);
  const argumentStale = useMemo(
    () =>
      // Gate on BOTH queries having resolved — while loading, an empty
      // claims/links array canonicalizes ≠ fingerprint and the banner
      // flashes a false "your claims changed" on every cold load
      // (caught by the headless smoke at reload+6s on a cold server).
      claimsData !== undefined && linksData !== undefined
        ? isArgumentStale(argumentAnalysis?.fingerprint, claimsData, linksData)
        : false,
    [argumentAnalysis, claimsData, linksData],
  );
  const landscapeStale = useMemo(() => {
    if (!scopeReady) return false;
    if (cachedLandscapeConceptId !== conceptId) return false;
    if (!cachedLandscapeFingerprint) return false;
    return isStale(
      cachedLandscapeFingerprint,
      { paperIds: activePaperIds, wsId: wsId ?? null },
      0,
    );
  }, [cachedLandscapeConceptId, cachedLandscapeFingerprint, conceptId, activePaperIds, wsId, scopeReady]);


  const statusInputs = useMemo(
    () => ({
      scopedPaperCount: displayPapers.length,
      scoredCount,
      isPaid,
      hasEvidenceOnClaims,
      chatIndexed: !!indexStatus?.is_indexed,
      // Partial (batch-pending) skeleton maps must NOT unlock downstream
      // tools — Landscape/Position on head-only paper_ids would present
      // incomplete analytics as complete (review MED 3). FieldDebateView
      // still renders the partial map straight from the query.
      hasDebate: isDebateMapComplete(fieldDebate.data),
      debateStale,
      debateRunning,
      debateRefreshing,
      hasAudit: !!gapData,
      auditStale,
      auditRunning,
      hasComparison: !!cachedComparison,
      comparisonRunning,
      hasLandscape:
        !!cachedLandscapeFingerprint && cachedLandscapeConceptId === conceptId,
      landscapeStale,
      hasPosition: !!positionAnalysis,
      positionStale,
      positionRunning,
      claimCount: claimsData?.length ?? 0,
      hasArgument: !!argumentAnalysis,
      argumentStale,
      argumentRunning,
    }),
    [
      displayPapers.length,
      scoredCount,
      isPaid,
      hasEvidenceOnClaims,
      indexStatus?.is_indexed,
      fieldDebate.data,
      debateStale,
      debateRunning,
      debateRefreshing,
      gapData,
      auditStale,
      auditRunning,
      cachedComparison,
      comparisonRunning,
      cachedLandscapeFingerprint,
      cachedLandscapeConceptId,
      conceptId,
      landscapeStale,
      positionAnalysis,
      positionStale,
      positionRunning,
      claimsData,
      argumentAnalysis,
      argumentStale,
      argumentRunning,
    ],
  );
  const statuses = useMemo(() => computeToolStatuses(statusInputs), [statusInputs]);
  const critique = useMemo(() => computeCritiqueStatuses(statusInputs), [statusInputs]);

  return {
    papersLoaded: trawledData !== undefined,
    allPapers,
    displayPapers,
    totalPapers,
    displayPaperIds,
    activePaperIds,
    scoredCount,
    wsId,
    statuses,
    debateStale,
    debateNeedsRefresh: needsDebateRefresh(debateStale, debateRunning, debateRefreshing),
    auditStale,
    positionStale,
    landscapeStale,
    debateOverlapPercent,
    auditOverlapPercent,
    debateRunning,
    debateBuildInFlight,
    debateRefreshing,
    auditRunning,
    comparisonRunning,
    fieldDebateData: fieldDebate.data,
    gapAnalysisData: gapData,
    cachedComparison,
    positionAnalysis,
    chatIndexed: !!indexStatus?.is_indexed,
    isPaid,
    hasEvidenceOnClaims,
    critique,
    argumentAnalysis,
    argumentStale,
  };
}
